import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Alert,
  FormControlLabel,
  Dialog,
  IconButton,
  Link,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  Close as CloseIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { EmailThreadComponent } from "../../components/EmailThreadComponent";
import { TimelineSidePanel } from "../shipments/TimelineSidePanel";
import { EditPromptsDialog } from "./EditPromptsDialog";
import {
  fetchEmailsByTag,
  recategorizeEmails,
  fetchCategoryDefinitions,
  fetchPropertyExtractionDefinitions,
} from "./api";
import type {
  EmailInfo,
  CategoryDefinitions,
  PropertyExtractionDefinitions,
} from "./types";
import AdminPage from "../AdminPage";

export const ShipmentEmailCategoryValidationPage: React.FC = () => {
  const navigate = useNavigate();

  // Tag and data loading
  const [tag, setTag] = useState("");
  const [limit, setLimit] = useState("1000");
  const [emails, setEmails] = useState<EmailInfo[]>([]);
  const [emailsByCategory, setEmailsByCategory] = useState<
    Record<string, EmailInfo[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Category filtering
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<
    Array<{ key: string; label: string }>
  >([]);

  // Selection
  const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(
    new Set()
  );
  const [emailIdsInput, setEmailIdsInput] = useState("");
  const [filterSelectedOnly, setFilterSelectedOnly] = useState(false);

  // Recategorization
  const [recategorizing, setRecategorizing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [categoryDefinitions, setCategoryDefinitions] =
    useState<CategoryDefinitions>({});
  const [propertyExtractionDefinitions, setPropertyExtractionDefinitions] =
    useState<PropertyExtractionDefinitions>({});
  const [loadingDefinitions, setLoadingDefinitions] = useState(true);

  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState("");

  // Timeline panel
  const [timelinePanelOpen, setTimelinePanelOpen] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(
    null
  );

  // Category refs for scrolling
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Scroll to category
  const scrollToCategory = (category: string) => {
    const element = categoryRefs.current[category];
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Fetch category and property extraction definitions on mount
  useEffect(() => {
    const loadDefinitions = async () => {
      try {
        const [categoryDefs, propertyDefs] = await Promise.all([
          fetchCategoryDefinitions(),
          fetchPropertyExtractionDefinitions(),
        ]);
        setCategoryDefinitions(categoryDefs);
        setPropertyExtractionDefinitions(propertyDefs);

        // Build available categories list with display names
        const categories = Object.entries(categoryDefs).map(([key, def]) => ({
          key,
          label: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        }));

        // Add "uncategorized" option
        categories.push({ key: "uncategorized", label: "Uncategorized" });

        setAvailableCategories(categories);

        // Select all categories by default
        setSelectedCategories(categories.map((c) => c.key));
      } catch (err) {
        console.error("Failed to load definitions:", err);
        setError("Failed to load definitions");
      } finally {
        setLoadingDefinitions(false);
      }
    };

    loadDefinitions();
  }, []);

  // Fetch emails by tag
  const handleFetchEmails = async () => {
    if (!tag.trim()) {
      setError("Please enter a tag");
      return;
    }

    const parsedLimit = parseInt(limit, 10);
    if (isNaN(parsedLimit) || parsedLimit <= 0) {
      setError("Please enter a valid limit (positive number)");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchEmailsByTag(
        tag.trim(),
        0,
        parsedLimit,
        selectedCategories.length > 0 ? selectedCategories : undefined
      );
      setEmails(response.emails);
      setEmailsByCategory(response.emailsByCategory);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch emails");
      console.error("Error fetching emails:", err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle email selection
  const handleToggleEmail = (emailId: string) => {
    const newSelection = new Set(selectedEmailIds);
    if (newSelection.has(emailId)) {
      newSelection.delete(emailId);
    } else {
      newSelection.add(emailId);
    }
    setSelectedEmailIds(newSelection);
    setEmailIdsInput(Array.from(newSelection).join(", "));
  };

  // Handle manual email IDs input
  const handleEmailIdsInputChange = (value: string) => {
    setEmailIdsInput(value);

    // Parse comma-separated IDs
    const ids = value
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    setSelectedEmailIds(new Set(ids));
  };

  // Handle email click
  const handleEmailClick = (emailId: string | null) => {
    if (emailId) {
      setSelectedEmailId(emailId);
      setEmailDialogOpen(true);
    }
  };

  // Handle shipment click
  const handleShipmentClick = (shipmentId: string) => {
    setSelectedShipmentId(shipmentId);
    setTimelinePanelOpen(true);
  };

  // Handle GPT request click - open in new tab
  const handleGptRequestClick = (requestId: string) => {
    window.open(`/www/admin/request/${requestId}`, "_blank");
  };

  // Handle recategorize and property extraction
  const handleRecategorize = async (
    categoryDefs: CategoryDefinitions,
    propertyDefs: PropertyExtractionDefinitions
  ) => {
    if (selectedEmailIds.size === 0) {
      setError("Please select at least one email");
      return;
    }

    setRecategorizing(true);
    setError(null);

    try {
      const response = await recategorizeEmails(
        Array.from(selectedEmailIds),
        categoryDefs,
        propertyDefs
      );

      if (response.success) {
        // Update the emails with new categories, GPT request IDs, and property extraction results
        const updatedEmails = emails.map((email) => {
          const result = response.results.find((r) => r.emailId === email.id);
          if (result && !result.error) {
            // Collect all GPT request IDs
            const newGptRequestIds = [...email.gptRequestIds];
            if (result.gptRequestId) {
              newGptRequestIds.push(result.gptRequestId);
            }

            // Collect new updates from property extraction results
            const newUpdates = [...email.updates];
            if (result.propertyExtractionResults) {
              result.propertyExtractionResults.forEach((propResult) => {
                if (propResult.gptRequestId) {
                  newGptRequestIds.push(propResult.gptRequestId);
                }

                // Use the full description from the backend
                if (propResult.description && propResult.description !== "No changes") {
                  newUpdates.push({
                    shipmentFeedId: propResult.shipmentFeedId,
                    description: propResult.description,
                  });
                }
              });
            }

            return {
              ...email,
              categories: result.categories || email.categories,
              gptRequestIds: Array.from(new Set(newGptRequestIds)),
              updates: newUpdates,
            };
          }
          return email;
        });

        setEmails(updatedEmails);

        // Regroup by category
        const newEmailsByCategory: Record<string, EmailInfo[]> = {};
        for (const email of updatedEmails) {
          const cats =
            email.categories.length > 0 ? email.categories : ["uncategorized"];

          for (const category of cats) {
            if (!newEmailsByCategory[category]) {
              newEmailsByCategory[category] = [];
            }
            newEmailsByCategory[category].push(email);
          }
        }
        setEmailsByCategory(newEmailsByCategory);

        // Update definitions for future use
        setCategoryDefinitions(categoryDefs);
        setPropertyExtractionDefinitions(propertyDefs);

        setEditDialogOpen(false);
      } else {
        setError("Processing failed");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to process emails"
      );
      console.error("Error processing emails:", err);
    } finally {
      setRecategorizing(false);
    }
  };

  // Get filtered emails for display
  const getFilteredEmails = (categoryEmails: EmailInfo[]) => {
    if (!filterSelectedOnly) {
      return categoryEmails;
    }

    return categoryEmails.filter((email) => selectedEmailIds.has(email.id));
  };

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Shipment Email Category Validation
        </Typography>

        {/* Tag Input Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2, flexWrap: "wrap" }}>
            <TextField
              label="Tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleFetchEmails();
                }
              }}
            />
            <Autocomplete
              multiple
              size="small"
              options={[
                { key: "__select_all__", label: "✓ Select All" },
                { key: "__select_none__", label: "✗ Select None" },
                ...availableCategories,
              ]}
              getOptionLabel={(option) => option.label}
              value={availableCategories.filter((cat) =>
                selectedCategories.includes(cat.key)
              )}
              onChange={(_, newValue, _reason, details) => {
                // Handle special select all/none options
                if (details?.option?.key === "__select_all__") {
                  setSelectedCategories(availableCategories.map((c) => c.key));
                  return;
                }
                if (details?.option?.key === "__select_none__") {
                  setSelectedCategories([]);
                  return;
                }

                // Normal selection
                setSelectedCategories(newValue.filter(v => v.key !== "__select_all__" && v.key !== "__select_none__").map((v) => v.key));
              }}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Categories (${selectedCategories.length} selected)`}
                  placeholder="Select categories"
                />
              )}
              renderTags={() => null}
              renderOption={(props, option, { selected }) => {
                // Special styling for select all/none
                const isSpecial = option.key === "__select_all__" || option.key === "__select_none__";

                return (
                  <li
                    {...props}
                    style={{
                      ...props.style,
                      fontWeight: isSpecial ? 600 : 400,
                      borderBottom: option.key === "__select_none__" ? "1px solid #e0e0e0" : undefined,
                      paddingBottom: option.key === "__select_none__" ? 8 : undefined,
                      marginBottom: option.key === "__select_none__" ? 8 : undefined,
                    }}
                  >
                    {!isSpecial && (
                      <Checkbox
                        checked={selected}
                        sx={{ marginRight: 1 }}
                      />
                    )}
                    {option.label}
                  </li>
                );
              }}
              sx={{ minWidth: 400 }}
              disableCloseOnSelect
              disabled={loadingDefinitions}
            />
            <TextField
              label="Max Emails"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              size="small"
              sx={{ width: 120 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleFetchEmails();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleFetchEmails}
              disabled={loading || loadingDefinitions}
            >
              {loading ? <CircularProgress size={24} /> : "Go"}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {emails.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Email IDs ({selectedEmailIds.size} selected):
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={emailIdsInput}
                onChange={(e) => handleEmailIdsInputChange(e.target.value)}
                placeholder="email1, email2, email3..."
                sx={{ mb: 2, fontFamily: "monospace" }}
              />

              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Button
                  variant="contained"
                  onClick={() => setEditDialogOpen(true)}
                  disabled={recategorizing}
                >
                  Edit Prompts
                </Button>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterSelectedOnly}
                      onChange={(e) => setFilterSelectedOnly(e.target.checked)}
                    />
                  }
                  label="Filter Selected Only"
                />

                {recategorizing && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Recategorizing...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Category Summary */}
        {Object.entries(emailsByCategory).length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CategoryIcon sx={{ mr: 1, fontSize: "1rem" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Categories:
                </Typography>
              </Box>
              {Object.entries(emailsByCategory)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, categoryEmails]) => {
                  const filteredCount = filterSelectedOnly
                    ? getFilteredEmails(categoryEmails).length
                    : categoryEmails.length;

                  return (
                    <Chip
                      key={category}
                      label={`${category} (${filteredCount})`}
                      onClick={() => scrollToCategory(category)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "primary.light",
                          color: "white",
                        },
                      }}
                    />
                  );
                })}
            </Box>
          </Paper>
        )}

        {/* Email Tables by Category */}
        {Object.entries(emailsByCategory).length > 0 && (
          <Box>
            {Object.entries(emailsByCategory).map(
              ([category, categoryEmails]) => {
                const filteredEmails = getFilteredEmails(categoryEmails);

                if (filteredEmails.length === 0 && filterSelectedOnly) {
                  return null;
                }

                return (
                  <Paper
                    key={category}
                    sx={{ mb: 3 }}
                    ref={(el) => (categoryRefs.current[category] = el)}
                  >
                    <Box sx={{ p: 2, backgroundColor: "grey.100" }}>
                      <Typography variant="h6">
                        {category} ({filteredEmails.length}{" "}
                        {filterSelectedOnly && `of ${categoryEmails.length} `}
                        emails)
                      </Typography>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>Subject</TableCell>
                            <TableCell>Shipment IDs</TableCell>
                            <TableCell>Updates</TableCell>
                            <TableCell>GPT Requests</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredEmails.map((email) => (
                            <TableRow
                              key={email.id}
                              sx={{
                                backgroundColor: selectedEmailIds.has(email.id)
                                  ? "action.selected"
                                  : undefined,
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedEmailIds.has(email.id)}
                                  onChange={() => handleToggleEmail(email.id)}
                                />
                              </TableCell>
                              <TableCell>
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() =>
                                    handleEmailClick(email.emailId)
                                  }
                                  sx={{ textAlign: "left" }}
                                >
                                  {email.subject || "(No subject)"}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {email.shipmentFeedIds.map((shipmentId) => (
                                  <Link
                                    key={shipmentId}
                                    component="button"
                                    variant="body2"
                                    onClick={() =>
                                      handleShipmentClick(shipmentId)
                                    }
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.85rem",
                                      mr: 1,
                                    }}
                                  >
                                    {shipmentId.substring(0, 8)}...
                                  </Link>
                                ))}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>
                                {email.updates && email.updates.length > 0 ? (
                                  <Box>
                                    {email.updates.map((update, idx) => (
                                      <Box
                                        key={idx}
                                        sx={{
                                          mb: 1,
                                          fontSize: "0.85rem",
                                          borderLeft: "2px solid",
                                          borderColor: "primary.main",
                                          pl: 1,
                                          py: 0.5,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          component="div"
                                          sx={{
                                            fontFamily: "monospace",
                                            color: "text.secondary",
                                            mb: 0.5,
                                          }}
                                        >
                                          {update.shipmentFeedId.substring(
                                            0,
                                            8
                                          )}
                                          ...
                                        </Typography>
                                        <Typography variant="body2">
                                          {update.description}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: "italic" }}
                                  >
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {email.gptRequestIds.map((requestId, idx) => (
                                  <Link
                                    key={idx}
                                    component="button"
                                    variant="body2"
                                    onClick={() =>
                                      handleGptRequestClick(requestId)
                                    }
                                    sx={{ mr: 1 }}
                                  >
                                    Request {idx + 1}
                                  </Link>
                                ))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                );
              }
            )}
          </Box>
        )}

        {/* Edit Prompts Dialog */}
        <EditPromptsDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSubmit={handleRecategorize}
          initialCategoryDefinitions={categoryDefinitions}
          initialPropertyExtractionDefinitions={propertyExtractionDefinitions}
        />

        {/* Email Dialog */}
        {emailDialogOpen && selectedEmailId && (
          <Dialog
            open={emailDialogOpen}
            onClose={() => setEmailDialogOpen(false)}
            maxWidth="lg"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                height: "90vh",
              },
            }}
          >
            <IconButton
              aria-label="close"
              onClick={() => setEmailDialogOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <EmailThreadComponent emailId={selectedEmailId} />
          </Dialog>
        )}

        {/* Timeline Side Panel */}
        <TimelineSidePanel
          open={timelinePanelOpen}
          onClose={() => setTimelinePanelOpen(false)}
          shipmentId={selectedShipmentId || undefined}
        />
      </Box>
    </AdminPage>
  );
};
